import { toHalfWidth } from "@/utils/formatCodeNumber";
import { z } from "zod";
import { Role } from "./type";

const SPACE_LETTER = " ";
export const REQUIRED_MESSAGE = "この項目は必須です。";

// Helper cho optional email validation
const optionalEmailValidation = z
  .string()
  .trim()
  .optional()
  .refine((val) => {
    // Nếu không có value hoặc empty string thì skip validation
    if (!val) return true;
    // Nếu có value thì validate email format
    const emailRegex =
      /^(?!\.)(?!.*\.\.)([a-z0-9_'+\-\.]*)[a-z0-9_+-]@([a-z0-9][a-z0-9\-]*\.)+[a-z]{2,}$/i;
    return emailRegex.test(val);
  }, "メールアドレスは無効な形式です。");

// Custom validation cho phone number
export const phoneNumberValidation = z
  .string(REQUIRED_MESSAGE)
  .refine((val) => {
    return /^[0-9０-９\-]*$/.test(val);
  }, "電話番号は数字のみ入力可能です。")
  .refine((val) => {
    if (!val) return false;
    return val.length >= 10;
  }, "電話番号は最低10桁の数字が必要です")
  .refine((val) => {
    if (!val) return false;
    // Kiểm tra phải bắt đầu bằng số 0
    const numbersOnly = toHalfWidth(val);
    return numbersOnly.startsWith("0");
  }, "電話番号は0で始まる必要があります");

export const phoneNumberOptionalValidation = z
  .string()
  .optional()
  .refine((val) => {
    if (!val) return true;
    return /^[0-9０-９\-]*$/.test(val);
  }, "電話番号は数字のみ入力可能です。")
  .refine((val) => {
    if (!val) return true;
    return val?.length >= 10;
  }, "電話番号は最低10桁の数字が必要です")
  .refine((val) => {
    if (!val) return true;
    // Kiểm tra phải bắt đầu bằng số 0
    const numbersOnly = toHalfWidth(val);
    return numbersOnly.startsWith("0");
  }, "電話番号は0で始まる必要があります");

const postCodeValidation = z
  .string(REQUIRED_MESSAGE)
  .optional()
  .refine((val) => {
    if (!val) return true;
    const numbersOnly = toHalfWidth(val).replace(/-/g, "");
    return numbersOnly.length >= 7;
  }, "郵便番号は7文字以上である必要があります。")
  .refine((val) => {
    if (!val) return true;
    const halfValue = toHalfWidth(val);
    return /\d{3}-\d{4}/.test(halfValue);
  }, "郵便番号は数字とハイフンのみ入力可能です")
  .refine((val) => {
    if (!val) return true;
    // Kiểm tra không được toàn dấu -
    const numbersOnly = val.replace(/-/g, "");
    return numbersOnly.length > 0;
  }, "郵便番号を入力してください");

export const passwordValidation = z
  .string(REQUIRED_MESSAGE)
  .min(1, REQUIRED_MESSAGE)
  .refine((val) => {
    // value toàn khoảng trắng
    if (!val.trim()?.length) return false;
    // value chứa khoảng trắng
    return !/\s/.test(val.trimEnd());
  }, "パスワードにスペース（空白文字）は使用できません。")
  .trim()
  .min(8, "パスワードは8文字以上で入力してください。")
  .max(255, "パスワードは255文字以内で入力してください。")
  .refine((val) => {
    return /[0-9]/.test(val);
  }, "パスワードには「半角英字小文字」「半角数字」をそれぞれ1文字以上混在させてください。")
  .refine((val) => {
    return /[a-zA-Z]/.test(val);
  }, "パスワードには「半角英字小文字」「半角数字」をそれぞれ1文字以上混在させてください。");

const companyCodeValidation = z
  .string(REQUIRED_MESSAGE)
  .min(1, REQUIRED_MESSAGE)
  .refine(
    (value) => {
      return /^[0-9０-９]*$/.test(value);
    },
    {
      message: "事業所コードは数字のみ入力可能です。",
    }
  );

const corporateCodeValidation = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value || value.length === 0) return true;
      else return /^[0-9０-９]*$/.test(value);
    },
    {
      message: "法人コードは数字のみ入力可能です。",
    }
  )
  .refine((value) => {
    if (!value || value.length === 0) return true;
    return value.length < 13;
  }, "法人コードは13文字である必要があります。");

const companyMemberSchema = z.object({
  corporateName: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  prefecture: z.coerce.number(REQUIRED_MESSAGE).optional(),
  postCode: postCodeValidation,
  address: z
    .string()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "住所は半角255文字以内で入力してください。"),
  phoneNumber: phoneNumberOptionalValidation,
  email: optionalEmailValidation,
  notes: z.string().optional(),
  companyCode: companyCodeValidation,
  corporateCode: corporateCodeValidation,
  series: z.string(SPACE_LETTER).min(1, SPACE_LETTER),
  business: z.string(SPACE_LETTER).min(1, SPACE_LETTER),
  representativePosition: z
    .string()
    .trim()
    .max(255, "代表者名は半角255文字以内で入力してください。")
    .optional(),
  joiningDate: z.string().optional().nullable(),
  unSubscribingDate: z.string().optional().nullable(),
  storeName: z
    .string()
    .trim()
    .max(255, "店舗名は半角255文字以内で入力してください。")
    .optional(),
  companyUser: z.object({
    userName: z.string().trim().min(1, REQUIRED_MESSAGE),
    phoneNumber: phoneNumberOptionalValidation,
    email: z
      .string(REQUIRED_MESSAGE)
      .trim()
      .email("メールアドレスは無効な形式です。")
      .min(1, REQUIRED_MESSAGE),
    password: passwordValidation,
    userRole: z.string().optional(),
    salesOffice: z.string().optional(),
    post: z.string().optional(),
    department: z.string().optional(),
  }),
});

const companyNonMemberSchema = z.object({
  companyCode: companyCodeValidation,
  corporateCode: corporateCodeValidation,
  corporateName: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  prefecture: z.coerce.number(REQUIRED_MESSAGE).optional(),
  postCode: postCodeValidation,
  address: z
    .string()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "住所は半角255文字以内で入力してください。"),
  phoneNumber: phoneNumberOptionalValidation,
  email: optionalEmailValidation,
  notes: z.string().optional(),
  companyUser: z.object({
    userName: z.string().trim().min(1, REQUIRED_MESSAGE),
    phoneNumber: phoneNumberOptionalValidation,
    email: z
      .string(REQUIRED_MESSAGE)
      .trim()
      .email("メールアドレスは無効な形式です。")
      .min(1, REQUIRED_MESSAGE),
    password: passwordValidation,
  }),
});

export const schema = z.discriminatedUnion("role", [
  z.object({
    role: z.literal(Role.MEMBER),
    companyData: companyMemberSchema,
  }),
  z.object({
    role: z.literal(Role.NON_MEMBER),
    companyData: companyNonMemberSchema,
  }),
]);

// Schema cho Edit mode - companyUser optional
const editCompanyMemberSchema = z.object({
  corporateName: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  prefecture: z.coerce.number(REQUIRED_MESSAGE).optional().nullable(),
  postCode: postCodeValidation,
  address: z
    .string()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "住所は半角255文字以内で入力してください。"),
  phoneNumber: phoneNumberOptionalValidation,
  email: optionalEmailValidation,
  notes: z.string().optional(),
  companyCode: companyCodeValidation,
  corporateCode: corporateCodeValidation,
  series: z.string(SPACE_LETTER).min(1, SPACE_LETTER),
  business: z.string(SPACE_LETTER).min(1, SPACE_LETTER),
  representativePosition: z
    .string()
    .trim()
    .max(255, "代表者名は半角255文字以内で入力してください。")
    .optional(),
  joiningDate: z.string().optional().nullable(),
  unSubscribingDate: z.string().optional().nullable(),
  storeName: z
    .string()
    .trim()
    .max(255, "店舗名は半角255文字以内で入力してください。")
    .optional(),
});

const editCompanyNonMemberSchema = z.object({
  companyCode: companyCodeValidation,
  corporateCode: corporateCodeValidation,
  corporateName: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  prefecture: z.coerce.number(REQUIRED_MESSAGE).optional(),
  postCode: postCodeValidation,
  address: z
    .string()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "住所は半角255文字以内で入力してください。"),
  phoneNumber: phoneNumberOptionalValidation,
  email: optionalEmailValidation,
  notes: z.string().optional(),
});

export const schemaEdit = z.discriminatedUnion("role", [
  z.object({
    role: z.literal(Role.MEMBER),
    companyData: editCompanyMemberSchema,
  }),
  z.object({
    role: z.literal(Role.NON_MEMBER),
    companyData: editCompanyNonMemberSchema,
  }),
]);
